// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/ProjectStatusPane.actionStatus.test.tsx
// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/ProjectStatusPane.compliance.test.tsx
// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/ProjectStatusPane.test.tsx
import {
  type AskPunttProjectData,
  type AskPunttComplianceSummary,
  type AskPunttTicketCompliance,
} from "@mg/schemas/src/christo/askPuntt";
import {
  ArrowSquareOut,
  BellRinging,
  CalendarBlank,
  Check,
  CheckCircle,
  CircleNotch,
  ClockCountdown,
  Lightning,
  MagnifyingGlass,
  PencilSimple,
  Play,
  Question,
  Rocket,
  Scales,
  Seal,
  ShieldCheck,
  Timer,
  Users,
  Warning,
  X,
  XCircle,
} from "@phosphor-icons/react";
import {
  Badge,
  Button,
  Callout,
  Flex,
  IconButton,
  Text,
  Tooltip,
} from "@radix-ui/themes";
import { useRouter } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  PANE_HEADER_ICON_SIZE,
  PaneHeader,
  PaneHeaderIconButton,
} from "./PaneHeader";
import { ReviewStatusCallout } from "./ReviewStatusCallout";

import { Markdown } from "../../../components/Markdown";
import { updateProjectAction } from "../../../services/askPuntt";
import { isDemoEnterprise } from "../../../utils/auth";
import { ticketRoute } from "../../tickets/routes/ticket/route";
import { useTicketViewData } from "../hooks/useTicketView";

const TICKET_STATUS_COLORS: Record<
  string,
  "green" | "orange" | "blue" | "gray" | "red"
> = {
  approved: "green",
  complete: "green",
  needs_review: "orange",
  wip: "blue",
  incomplete: "red",
};

const TICKET_STATUS_LABELS: Record<string, string> = {
  approved: "Approved",
  complete: "Complete",
  needs_review: "Needs Review",
  wip: "In Progress",
  incomplete: "Incomplete",
};

interface ProjectStatusPaneProps {
  projectData: AskPunttProjectData;
  activeTicketId?: string | null;
  ticketIds?: string[];
  onTicketSelect?: (ticketId: string) => void;
  chatId: string;
  chatCreatedAt?: string;
  onDismiss?: () => void;
  onSendMessage?: (message: string) => void;
  onProjectDataUpdate?: (data: AskPunttProjectData) => void;
  actionsDisabled?: boolean;
  isLoading?: boolean;
  /**
   * True while the Vitalora killer-flow typewriter is still revealing
   * the parallel review. When set, ticket cards render in a "Reviewing
   * …" state (no severity badge, risk chip, or comment count) and the
   * findings-summary block is replaced with a "Findings will appear as
   * each reviewer completes" placeholder. The pane itself stays
   * mounted so the buyer sees the project shape from t=0; the
   * per-card resolution happens when streaming finishes.
   */
  isKillerFlowStreaming?: boolean;
}

function stageBarClasses(stage: { status: string; color: string }): string {
  const base = "h-2.5 flex-1 rounded-full transition-all duration-300";
  switch (stage.status) {
    case "done":
      return `${base} bg-malachite-500`;
    case "active":
      return `${base} bg-puntt-gold-9 animate-pulse`;
    case "needs_review":
      return `${base} bg-puntt-red-9 ring-2 ring-puntt-red-5`;
    case "blocked":
      return `${base} bg-puntt-red-9`;
    default:
      return `${base} bg-puntt-neutral-gray-5`;
  }
}

function stageLabelWeight(status: string): "bold" | "medium" | "regular" {
  if (status === "needs_review" || status === "active") return "bold";
  if (status === "done") return "medium";
  return "regular";
}

function stageLabelColor(status: string): string {
  if (status === "needs_review") return "text-puntt-red-11";
  if (status === "done") return "text-malachite-700";
  if (status === "active") return "text-puntt-gold-11";
  return "text-puntt-neutral-gray-9";
}

export function formatDuration(ms: number): string {
  const absMs = Math.abs(ms);
  const hours = Math.floor(absMs / (1000 * 60 * 60));
  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;

  if (days === 0 && hours === 0) return "< 1h";
  if (days === 0) return `${hours}h`;
  if (remainingHours === 0) return `${days}d`;
  return `${days}d ${remainingHours}h`;
}

export function deadlineColor(msRemaining: number): string {
  if (msRemaining < 0) return "text-puntt-red-9";
  const daysRemaining = msRemaining / (1000 * 60 * 60 * 24);
  if (daysRemaining <= 2) return "text-puntt-gold-11";
  return "text-puntt-neutral-gray-12";
}

export function deadlineBg(msRemaining: number): string {
  if (msRemaining < 0) return "bg-puntt-red-2 border-puntt-red-5";
  const daysRemaining = msRemaining / (1000 * 60 * 60 * 24);
  if (daysRemaining <= 2) return "bg-puntt-gold-2 border-puntt-gold-5";
  return "bg-puntt-neutral-gray-2 border-puntt-neutral-gray-6";
}

function ReviewerRow({
  reviewer,
  onOverride,
  actionsDisabled,
}: {
  reviewer: AskPunttProjectData["routing"][0];
  onOverride?: (role: string) => void;
  actionsDisabled?: boolean;
}) {
  const { t } = useTranslation("ui");
  const isDone = reviewer.status === "done";
  const needsReview = reviewer.status === "needs_review";
  const isActive = reviewer.status === "active";
  const isPending = reviewer.status === "pending";
  const [notified, setNotified] = useState(false);

  return (
    <Flex
      align="center"
      gap="3"
      className={`py-2.5 ${needsReview ? "rounded-lg border-l-2 border-l-puntt-red-9 pl-2.5" : ""}`}
      data-testid="project-status-reviewer-row"
    >
      <div
        className={`flex size-8 shrink-0 items-center justify-center rounded-full ${
          isDone
            ? "bg-malachite-100"
            : needsReview
              ? "bg-puntt-red-3"
              : "bg-puntt-neutral-gray-4"
        }`}
      >
        <Text
          size="1"
          weight="bold"
          className={
            isDone
              ? "text-malachite-700"
              : needsReview
                ? "text-puntt-red-11"
                : "text-puntt-neutral-gray-11"
          }
        >
          {reviewer.initials}
        </Text>
      </div>
      <div className="min-w-0 flex-1">
        <Text
          size="2"
          weight={needsReview || isActive ? "bold" : "medium"}
          className={
            isPending
              ? "text-puntt-neutral-gray-9"
              : "text-puntt-neutral-gray-12"
          }
        >
          {reviewer.name}
        </Text>
        <Text as="span" size="1" className="ml-1 text-puntt-neutral-gray-9">
          · {reviewer.role}
        </Text>
      </div>
      {isDone ? (
        <>
          <Badge size="1" variant="soft" color="green">
            {t("humanReviewNotNeeded")}
          </Badge>
          <Check
            size={14}
            weight="bold"
            className="shrink-0 text-malachite-600"
          />
        </>
      ) : needsReview ? (
        <Flex align="center" gap="2">
          {notified ? (
            <Badge size="1" variant="soft" color="amber">
              {t("notified")}
            </Badge>
          ) : (
            <Badge size="1" variant="soft" color="red">
              {t("needsReview")}
            </Badge>
          )}
          {!notified && (
            <Tooltip content={`Notify ${reviewer.name}`}>
              <IconButton
                data-testid="project-status-pane-icon-button-218"
                size="1"
                variant="ghost"
                color="amber"
                className="cursor-pointer"
                disabled={actionsDisabled}
                onClick={() => setNotified(true)}
              >
                <BellRinging size={14} weight="fill" />
              </IconButton>
            </Tooltip>
          )}
          {onOverride && reviewer.role && (
            <Tooltip content={`Override ${reviewer.role}`}>
              <IconButton
                data-testid="project-status-pane-icon-button-232"
                size="1"
                variant="ghost"
                color="gray"
                className="cursor-pointer"
                disabled={actionsDisabled}
                onClick={() => onOverride(reviewer.role)}
              >
                <ShieldCheck size={14} weight="fill" />
              </IconButton>
            </Tooltip>
          )}
        </Flex>
      ) : isActive ? (
        <>
          <Badge size="1" variant="soft" color="yellow">
            {t("inReviewLower")}
          </Badge>
          <span className="size-2.5 shrink-0 animate-pulse rounded-full bg-puntt-gold-9" />
        </>
      ) : (
        <>
          <Badge size="1" variant="soft" color="gray">
            {t("pending")}
          </Badge>
          <span className="size-2.5 shrink-0 rounded-full bg-puntt-neutral-gray-5" />
        </>
      )}
    </Flex>
  );
}

/**
 * Pick the thumbnail URL for a ticket card in the project list.
 *
 * Demo enterprises get a wider fallback chain (down to the full-res `source`)
 * so seeded tickets render an image before the asset processor has run. Real
 * tenants also fall back to `lastBoard.thumbnails[0]` when `thumbnailUrl` is
 * missing — but NEVER to `source`, since `source` is the full-resolution PDF
 * and would blow the row layout + bandwidth budget. Bug surfaced via Nestle
 * daily-test feedback (CSV #13, 2026-05-11).
 */
export function pickCardThumbUrl(
  firstReviewFile: { source?: string; thumbnailUrl?: string } | undefined,
  lastBoard: { thumbnails?: string[] } | undefined,
  isDemoEnterprise: boolean,
): string | undefined {
  if (isDemoEnterprise) {
    return (
      firstReviewFile?.thumbnailUrl ??
      lastBoard?.thumbnails?.[0] ??
      firstReviewFile?.source
    );
  }
  return firstReviewFile?.thumbnailUrl ?? lastBoard?.thumbnails?.[0];
}

export function deriveTicketDisplayStatus(
  ticketStatus: string,
  stages: AskPunttProjectData["stages"],
  findingSeverity?: "clean" | "minor" | "major",
): { label: string; color: "green" | "orange" | "blue" | "gray" | "red" } {
  if (ticketStatus === "approved" || ticketStatus === "complete") {
    return { label: "Approved", color: "green" };
  }

  // Per-ticket finding severity is the only signal that belongs to THIS
  // ticket. Project-level `stages` describe the whole project and must not
  // flip an individual ticket to green — otherwise one completed ticket
  // would make every card read "Human review not needed".
  if (findingSeverity === "major") {
    return { label: "Human review needed", color: "orange" };
  }
  if (findingSeverity === "minor") {
    return { label: "Human review recommended", color: "orange" };
  }
  if (findingSeverity === "clean") {
    return { label: "Human review not needed", color: "green" };
  }

  // No per-ticket finding yet — describe review state without leaking other
  // tickets' completion into this card.
  const anyActive = stages.some((s) => s.status === "active");
  if (anyActive) {
    return { label: "Review In Progress", color: "blue" };
  }
  return {
    label: TICKET_STATUS_LABELS[ticketStatus] ?? ticketStatus,
    color: TICKET_STATUS_COLORS[ticketStatus] ?? "gray",
  };
}

function TicketCardSkeleton() {
  return (
    <div
      className="mb-3 flex animate-pulse items-center gap-3 rounded-lg border border-puntt-neutral-gray-6 p-3"
      data-testid="project-status-ticket-card-skeleton"
    >
      <div className="size-10 shrink-0 rounded-md bg-puntt-neutral-gray-4" />
      <div className="min-w-0 flex-1">
        <div className="mb-1.5 h-4 w-3/4 rounded bg-puntt-neutral-gray-4" />
        <div className="h-3 w-1/3 rounded bg-puntt-neutral-gray-4" />
      </div>
    </div>
  );
}

type BucketKey = "non_compliant" | "unsure" | "derivative" | "approved";

type BucketSpec = {
  key: BucketKey;
  label: string;
  tooltip: string;
  color: "red" | "blue" | "amber" | "green";
  Icon: typeof Warning;
};

// Severity-ordered: most-actionable first. The reviewer's eye lands on
// action-required buckets before anything else.
const COMPLIANCE_BUCKETS: BucketSpec[] = [
  {
    key: "non_compliant",
    label: "Non-compliant",
    tooltip: "Claim does not match approved corpus or violates regulations.",
    color: "red",
    Icon: XCircle,
  },
  {
    key: "unsure",
    label: "Needs review",
    tooltip:
      "AI could not confidently verdict this claim — human judgment required.",
    color: "blue",
    Icon: Question,
  },
  {
    key: "derivative",
    label: "Derivative",
    tooltip: "Acceptable variation of approved wording.",
    color: "amber",
    Icon: Scales,
  },
  {
    key: "approved",
    label: "Approved",
    tooltip: "Claim matches approved corpus.",
    color: "green",
    Icon: CheckCircle,
  },
];

export function ComplianceSummaryBlock({
  summary,
}: {
  summary: AskPunttComplianceSummary;
}) {
  const { t } = useTranslation("ui");
  const {
    total,
    non_compliant,
    unsure,
    derivative,
    approved,
    isComplete,
    ticketsReviewed,
    ticketsExpected,
    ticketsWithCompliance,
  } = summary;

  const allClean =
    total > 0 && non_compliant === 0 && unsure === 0 && derivative === 0;

  // Progress line — disambiguates "AI automation progress" from human sign-off
  const progressLine = isComplete
    ? `${ticketsReviewed} of ${ticketsExpected} tickets AI-reviewed`
    : `In progress — ${ticketsReviewed} of ${ticketsExpected} tickets AI-reviewed`;

  if (allClean) {
    return (
      <div
        className="border-green-200 bg-green-50 mb-5 rounded-lg border p-3"
        data-testid="compliance-summary-block"
      >
        <Flex align="start" gap="2">
          <CheckCircle
            size={14}
            weight="fill"
            className="text-green-600 mt-0.5 shrink-0"
          />
          <Text size="2" weight="medium" className="text-green-800">
            {t("projectStatusPane.allClaimsApproved", {
              approved,
              tickets: ticketsWithCompliance,
            })}
          </Text>
        </Flex>
      </div>
    );
  }

  return (
    <div
      className="mb-5 rounded-lg border border-puntt-neutral-gray-6 bg-puntt-neutral-gray-2/30 p-4"
      data-testid="compliance-summary-block"
    >
      <Flex align="center" gap="2" className="mb-1">
        <ShieldCheck size={14} weight="fill" className="text-puntt-accent-9" />
        <Text size="2" weight="bold" className="text-puntt-neutral-gray-12">
          {t("complianceVerdicts")}
        </Text>
      </Flex>
      <Text size="1" className="mb-3 block text-puntt-neutral-gray-10">
        {t("claimLevelOutcomes")}
      </Text>
      <Flex gap="2" wrap="wrap" className="mb-2">
        {COMPLIANCE_BUCKETS.map(({ key, label, tooltip, color, Icon }) => (
          <Tooltip key={key} content={tooltip}>
            <Badge
              size="1"
              variant="soft"
              color={color}
              data-testid={`compliance-chip-${key}`}
            >
              <Icon size={11} weight="fill" aria-hidden="true" />
              <span>
                {label}: {summary[key]}
              </span>
            </Badge>
          </Tooltip>
        ))}
      </Flex>
      <Text
        size="1"
        className={
          isComplete
            ? "text-puntt-neutral-gray-9"
            : "font-medium text-puntt-neutral-gray-10"
        }
      >
        {progressLine}
      </Text>
    </div>
  );
}

/**
 * Client-side heuristic for the rare case where aggregation silently failed.
 * If any per-ticket finding indicates compliance comments exist but the
 * project-level complianceSummary is absent, surface a quiet retry hint.
 * The backend try/except in update_project_stages_activity can leave the
 * summary unset on error; this catches that without needing a new error field.
 */
export function ComplianceAggregationErrorHint({
  projectData,
}: {
  projectData: AskPunttProjectData;
}) {
  const { t } = useTranslation("ui");
  // Only show when per-ticket tallies exist but the rollup is missing —
  // a genuine inconsistency rather than a non-compliance project.
  const hasPerTicket = (projectData.ticketCompliance ?? []).length > 0;
  const hasSummary = Boolean(projectData.complianceSummary);
  if (!hasPerTicket || hasSummary) return null;
  return (
    <div
      className="mb-5 rounded-lg border border-puntt-neutral-gray-6 bg-puntt-neutral-gray-2/30 p-3"
      data-testid="compliance-aggregation-error"
    >
      <Text size="1" className="text-puntt-neutral-gray-10">
        {t("projectStatusPane.verdictTallyUnavailable")}
      </Text>
    </div>
  );
}

export function TicketComplianceSparkline({
  tally,
}: {
  tally: AskPunttTicketCompliance;
}) {
  // Compact per-ticket verdict summary. Replaces the severity dot when the
  // ticket has compliance verdicts — one line, more information than the dot.
  // Zero buckets omitted. Each glyph has a text title so screen readers and
  // color-blind users get the meaning, not just the color.
  const parts: {
    key: string;
    glyph: string;
    count: number;
    title: string;
    color: string;
  }[] = [
    {
      key: "non_compliant",
      glyph: "⚠",
      count: tally.non_compliant,
      title: "Non-compliant",
      color: "text-puntt-red-11",
    },
    {
      key: "unsure",
      glyph: "?",
      count: tally.unsure,
      title: "Needs review",
      color: "text-puntt-accent-11",
    },
    {
      key: "derivative",
      glyph: "≈",
      count: tally.derivative,
      title: "Derivative",
      color: "text-puntt-gold-11",
    },
    {
      key: "approved",
      glyph: "✓",
      count: tally.approved,
      title: "Approved",
      color: "text-malachite-700",
    },
  ];
  const visible = parts.filter((p) => p.count > 0);
  if (visible.length === 0) return null;
  return (
    <Tooltip
      content={`Claims: ${tally.non_compliant} non-compliant, ${tally.unsure} needs review, ${tally.derivative} derivative, ${tally.approved} approved`}
    >
      <Text
        as="span"
        size="1"
        className="text-puntt-neutral-gray-10"
        data-testid="ticket-compliance-sparkline"
      >
        {visible.map((p, i) => (
          <span
            key={p.key}
            title={`${p.title}: ${p.count}`}
            className={p.color}
          >
            {i > 0 ? " · " : ""}
            {p.glyph}
            {p.count}
          </span>
        ))}
      </Text>
    </Tooltip>
  );
}

function TicketCard({
  ticketId,
  stages,
  onSelect,
  ticketFindings,
  ticketCompliance,
  isReviewing,
  showExternalLink = true,
}: {
  ticketId: string;
  stages: AskPunttProjectData["stages"];
  onSelect?: (ticketId: string) => void;
  ticketFindings?: AskPunttProjectData["ticketFindings"];
  ticketCompliance?: AskPunttProjectData["ticketCompliance"];
  /**
   * When true the card hides its severity/risk/comment badges and
   * shows a pulsing "Reviewing…" pill instead. Used during the
   * Vitalora killer-flow typewriter so the resolution lands when
   * the parallel review is visibly complete in the chat, not the
   * moment `projectData` arrives on the wire.
   */
  isReviewing?: boolean;
  /** When false, hides the open-in-new-tab affordance (multi-review rail). */
  showExternalLink?: boolean;
}) {
  const { t } = useTranslation("ui");
  const router = useRouter();
  // Build through the router so the URL respects the configured basepath
  // (e.g. `/ticket-canvas-shapes-guard` on epic environments). A raw
  // `/tickets/...` href bypasses the SPA's basepath.
  const ticketHref = showExternalLink
    ? router.buildLocation({
        to: ticketRoute.to,
        params: { ticketId },
        search: { version: 0 },
      }).href
    : null;
  const { ticket, isLoading } = useTicketViewData(ticketId);

  if (isLoading) return <TicketCardSkeleton />;
  if (!ticket) return null;

  const rawStatus = ticket.status ?? "wip";
  const finding = ticketFindings?.find((f) => f.ticketId === ticketId);
  const complianceTally = ticketCompliance?.find(
    (t) => t.ticketId === ticketId,
  );
  const { label, color } = deriveTicketDisplayStatus(
    rawStatus,
    stages,
    finding?.severity,
  );
  const lastBoard = ticket.revisionBoards
    ?.filter((b: { deletedAt?: unknown }) => !b.deletedAt)
    ?.at(-1) as
    | {
        thumbnails?: string[];
        reviewFiles?: { source?: string; thumbnailUrl?: string }[];
      }
    | undefined;
  const firstReviewFile = lastBoard?.reviewFiles?.[0];
  const thumbUrl = pickCardThumbUrl(
    firstReviewFile,
    lastBoard,
    isDemoEnterprise(),
  );

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect?.(ticketId)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect?.(ticketId);
        }
      }}
      className="mb-3 flex cursor-pointer items-center gap-3 rounded-lg border border-puntt-neutral-gray-6 p-3 transition-colors hover:bg-puntt-neutral-gray-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-puntt-accent-9"
      data-testid="project-status-ticket-card"
      aria-label={t("projectStatusPane.viewTicket", { title: ticket.title })}
    >
      {thumbUrl && (
        <img
          src={thumbUrl}
          alt=""
          className="size-10 shrink-0 rounded-md object-cover"
        />
      )}
      <div className="min-w-0 flex-1 overflow-hidden">
        <Text
          as="div"
          size="2"
          weight="medium"
          className="w-full min-w-0 truncate text-puntt-neutral-gray-12"
        >
          {ticket.title}
        </Text>
        <Flex align="center" gap="2" className="mt-0.5">
          {isReviewing ? (
            <Badge
              size="1"
              variant="soft"
              color="amber"
              data-testid="ticket-reviewing-pill"
            >
              <span className="mr-1 inline-block size-1.5 animate-pulse rounded-full bg-puntt-gold-9 align-middle" />
              {t("ticketViewPane.reviewing")}
            </Badge>
          ) : (
            <>
              <Badge size="1" variant="soft" color={color}>
                {label}
              </Badge>
              {finding?.riskScore ? (
                // Per-asset risk rollup (low/medium/high). Phase 1 of the
                // 3-tier asset flow plan. Bill's specific demo ask: "these
                // three came back low / medium / high".
                <Badge
                  size="1"
                  variant="soft"
                  color={
                    finding.riskScore === "high"
                      ? "red"
                      : finding.riskScore === "medium"
                        ? "amber"
                        : "green"
                  }
                  data-testid={`ticket-risk-chip-${finding.riskScore}`}
                >
                  {finding.riskScore === "high"
                    ? t("projectStatusPane.highRisk")
                    : finding.riskScore === "medium"
                      ? t("projectStatusPane.mediumRisk")
                      : t("projectStatusPane.lowRisk")}
                </Badge>
              ) : null}
              {finding && finding.commentCount > 0 && (
                <Badge size="1" variant="soft" color="gray">
                  {finding.commentCount}{" "}
                  {finding.commentCount === 1 ? "comment" : "comments"}
                </Badge>
              )}
              {complianceTally ? (
                <TicketComplianceSparkline tally={complianceTally} />
              ) : finding ? (
                // Fall back to severity dot only when there's no compliance data —
                // prevents double-encoding the same signal.
                <div
                  className={`size-2 rounded-full ${
                    finding.severity === "major"
                      ? "bg-red-500"
                      : finding.severity === "minor"
                        ? "bg-amber-500"
                        : "bg-green-500"
                  }`}
                />
              ) : null}
            </>
          )}
        </Flex>
      </div>
      {showExternalLink && ticketHref ? (
        <a
          data-testid="project-status-pane-a-651"
          href={ticketHref}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="shrink-0 rounded p-1 text-puntt-neutral-gray-9 transition-colors hover:bg-puntt-neutral-gray-3 hover:text-puntt-neutral-gray-12"
          aria-label={t("openTicketNewTab")}
        >
          <ArrowSquareOut size={14} />
        </a>
      ) : null}
    </div>
  );
}

function TimelineBlock({
  chatCreatedAt,
  targetDeadline,
  completedAt,
}: {
  chatCreatedAt?: string;
  targetDeadline?: string;
  completedAt?: string;
}) {
  const { t } = useTranslation("ui");
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (completedAt) return;
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, [completedAt]);

  const timeline = useMemo(() => {
    const created = chatCreatedAt ? new Date(chatCreatedAt).getTime() : null;
    const deadline = targetDeadline ? new Date(targetDeadline).getTime() : null;
    const completed = completedAt ? new Date(completedAt).getTime() : null;

    return { now, created, deadline, completed };
  }, [now, chatCreatedAt, targetDeadline, completedAt]);

  const { created, deadline, completed } = timeline;

  if (completed && created) {
    const actualDuration = completed - created;
    const expectedDuration = deadline ? deadline - created : null;
    const delta = expectedDuration ? expectedDuration - actualDuration : null;

    return (
      <div className="mb-5 space-y-2" data-testid="project-status-timeline">
        <Flex
          align="center"
          gap="2"
          className="rounded-lg border border-malachite-200 bg-malachite-50 px-3 py-2.5"
        >
          <CheckCircle
            size={16}
            weight="fill"
            className="shrink-0 text-malachite-600"
          />
          <div className="min-w-0 flex-1">
            <Text size="2" weight="medium" className="text-malachite-800">
              {t("projectStatusPane.completedIn", {
                duration: formatDuration(actualDuration),
              })}
            </Text>
          </div>
        </Flex>
        {delta !== null && (
          <Flex justify="between" align="center" className="px-1">
            <Text size="1" className="text-puntt-neutral-gray-9">
              {t("projectStatusPane.vsDeadline")}
            </Text>
            <Text
              size="2"
              weight="bold"
              className={delta >= 0 ? "text-malachite-700" : "text-puntt-red-9"}
            >
              {delta >= 0
                ? `${formatDuration(delta)} ahead`
                : `${formatDuration(Math.abs(delta))} behind`}
            </Text>
          </Flex>
        )}
      </div>
    );
  }

  return (
    <div className="mb-5 space-y-2" data-testid="project-status-timeline">
      {created && (
        <Flex justify="between" align="center" className="px-1">
          <Flex align="center" gap="1.5">
            <Timer size={14} className="text-puntt-neutral-gray-9" />
            <Text size="2" className="text-puntt-neutral-gray-11">
              {t("elapsed")}
            </Text>
          </Flex>
          <Text size="2" weight="medium" className="text-puntt-neutral-gray-12">
            {formatDuration(now - created)}
          </Text>
        </Flex>
      )}
      {deadline && (
        <Flex
          align="start"
          gap="2"
          className={`rounded-lg border px-3 py-2.5 ${deadlineBg(deadline - now)}`}
        >
          <CalendarBlank
            size={14}
            className={`mt-0.5 shrink-0 ${deadlineColor(deadline - now)}`}
          />
          <div className="min-w-0 flex-1">
            <Text
              size="2"
              weight="medium"
              className={deadlineColor(deadline - now)}
            >
              {deadline - now > 0
                ? t("projectStatusPane.deadlineIn", {
                    duration: formatDuration(deadline - now),
                  })
                : t("projectStatusPane.deadlinePassedAgo", {
                    duration: formatDuration(now - deadline),
                  })}
            </Text>
          </div>
          {deadline - now > 0 && (
            <ClockCountdown
              size={14}
              className={`mt-0.5 shrink-0 ${deadlineColor(deadline - now)}`}
            />
          )}
        </Flex>
      )}
    </div>
  );
}

export function ActionStatus({
  stages,
  routing,
  hasTicket,
  onSendMessage,
  onCompleteProject,
  onOverrideAll,
  actionsDisabled,
}: {
  stages: AskPunttProjectData["stages"];
  routing: AskPunttProjectData["routing"];
  hasTicket: boolean;
  onSendMessage?: (message: string) => void;
  onCompleteProject?: () => void;
  onOverrideAll?: () => void;
  actionsDisabled?: boolean;
}) {
  const { t } = useTranslation("ui");
  const [reviewersNotified, setReviewersNotified] = useState(false);
  const needsReview = stages.filter((s) => s.status === "needs_review");
  const active = stages.filter((s) => s.status === "active");
  const blocked = stages.filter((s) => s.status === "blocked");
  const allDone = stages.length > 0 && stages.every((s) => s.status === "done");
  const allPending =
    stages.length > 0 && stages.every((s) => s.status === "pending");
  const nonFinal = stages.filter(
    (s) => (s.name ?? "").toLowerCase() !== "final",
  );
  const readyForCompletion =
    !allDone &&
    nonFinal.length > 0 &&
    nonFinal.every((s) => s.status === "done");

  if (allDone) {
    return (
      <ReviewStatusCallout
        color="green"
        testid="project-status-action-banner"
        className="mb-5"
        icon={<CheckCircle size={16} weight="fill" />}
      >
        <Callout.Text weight="medium">{t("projectCompleted")}</Callout.Text>
      </ReviewStatusCallout>
    );
  }

  if (readyForCompletion) {
    return (
      <div
        className="mb-5 space-y-3"
        data-testid="project-status-action-banner"
      >
        <ReviewStatusCallout
          color="green"
          testid="project-status-ready-banner"
          icon={<Check size={16} weight="bold" />}
        >
          <Callout.Text weight="medium">
            {t("projectStatusPane.allStagesClear")}
          </Callout.Text>
        </ReviewStatusCallout>
        {onCompleteProject && (
          <Button
            data-testid="project-status-pane-button-850"
            size="2"
            color="green"
            className="w-full cursor-pointer"
            disabled={actionsDisabled}
            onClick={onCompleteProject}
          >
            <CheckCircle size={14} weight="fill" />
            {t("completeProject")}
          </Button>
        )}
      </div>
    );
  }

  if (blocked.length > 0) {
    return (
      <ReviewStatusCallout
        color="red"
        testid="project-status-action-banner"
        className="mb-5"
        icon={<Warning size={16} weight="fill" />}
      >
        <Callout.Text weight="medium">
          {t("projectStatusPane.blocked", {
            stages: blocked.map((s) => s.name ?? t("unknown")).join(" & "),
          })}
        </Callout.Text>
      </ReviewStatusCallout>
    );
  }

  if (needsReview.length > 0) {
    const reviewerNames = routing
      .filter((r) => {
        const role = (r.role ?? "").toLowerCase();
        return needsReview.some((s) => {
          const name = (s.name ?? "").toLowerCase();
          return (
            role === name || role.startsWith(name) || name.startsWith(role)
          );
        });
      })
      .map((r) => r.name ?? "");

    return (
      <div
        className="mb-5 space-y-3"
        data-testid="project-status-action-banner"
      >
        <ReviewStatusCallout
          color="amber"
          testid="project-status-needs-review-banner"
          icon={<Warning size={16} weight="fill" />}
        >
          <Callout.Text weight="medium">
            {t("projectStatusPane.stagesNeedHumanReview", {
              count: needsReview.length,
              stages: needsReview
                .map((s) => s.name ?? t("unknown"))
                .join(" & "),
            })}
          </Callout.Text>
          {reviewerNames.length > 0 && (
            <Text as="p" size="1" color="gray" className="mt-0.5">
              {t("projectStatusPane.assignedTo", {
                names: reviewerNames.join(", "),
              })}
            </Text>
          )}
        </ReviewStatusCallout>
        <Flex gap="2">
          <Button
            data-testid="project-status-pane-button-924"
            size="2"
            variant="outline"
            color={reviewersNotified ? "green" : "amber"}
            className="flex-1 cursor-pointer"
            disabled={actionsDisabled || reviewersNotified}
            onClick={() => setReviewersNotified(true)}
          >
            {reviewersNotified ? (
              <>
                <Check size={14} weight="bold" />
                {t("reviewersNotified")}
              </>
            ) : (
              <>
                <BellRinging size={14} weight="fill" />
                {t("notifyReviewers")}
              </>
            )}
          </Button>
          {onOverrideAll && (
            <Button
              data-testid="project-status-pane-button-945"
              size="2"
              variant="outline"
              className="flex-1 cursor-pointer"
              disabled={actionsDisabled}
              onClick={onOverrideAll}
            >
              <ShieldCheck size={14} weight="fill" />
              {t("overrideApproveAll")}
            </Button>
          )}
        </Flex>
      </div>
    );
  }

  if (active.length > 0) {
    return (
      <ReviewStatusCallout
        color="blue"
        testid="project-status-action-banner"
        className="mb-5"
        icon={
          <span className="bg-blue-9 size-2.5 animate-pulse rounded-full motion-reduce:animate-none" />
        }
      >
        <Callout.Text weight="medium">
          {t("projectStatusPane.aiReviewInProgressAcross", {
            count: active.length,
          })}
        </Callout.Text>
      </ReviewStatusCallout>
    );
  }

  if (allPending && !hasTicket) {
    return (
      <div
        className="mb-5 space-y-3"
        data-testid="project-status-action-banner"
      >
        <ReviewStatusCallout
          color="indigo"
          testid="project-status-ai-plan-banner"
          icon={<Lightning size={16} weight="fill" />}
        >
          <Callout.Text weight="medium">
            {t("projectStatusPane.aiGeneratedPlan")}
          </Callout.Text>
        </ReviewStatusCallout>
        {onSendMessage && (
          <Flex gap="2">
            <Button
              data-testid="project-status-pane-button-998"
              size="2"
              className="flex-1 cursor-pointer"
              disabled={actionsDisabled}
              onClick={() => onSendMessage("Start project")}
            >
              <Play size={14} weight="fill" />
              {t("approveAndStart")}
            </Button>
            <Button
              data-testid="project-status-pane-button-1007"
              size="2"
              variant="outline"
              className="cursor-pointer"
              disabled={actionsDisabled}
              onClick={() =>
                onSendMessage("I want to change something about this plan")
              }
            >
              <PencilSimple size={14} weight="bold" />
              {t("edit")}
            </Button>
          </Flex>
        )}
      </div>
    );
  }

  if (allPending) {
    return (
      <ReviewStatusCallout
        color="blue"
        testid="project-status-action-banner"
        className="mb-5"
        icon={
          <span className="bg-blue-9 size-2.5 animate-pulse rounded-full motion-reduce:animate-none" />
        }
      >
        <Callout.Text weight="medium">{t("aiReviewInProgress")}</Callout.Text>
      </ReviewStatusCallout>
    );
  }

  return null;
}

function ConfidenceBadge({ confidence }: { confidence: number }) {
  let color: "green" | "orange" | "red" | "gray";
  let label: string;
  if (confidence >= 85) {
    color = "green";
    label = "High confidence";
  } else if (confidence >= 60) {
    color = "orange";
    label = "Medium confidence";
  } else if (confidence >= 30) {
    color = "red";
    label = "Low confidence";
  } else {
    color = "gray";
    label = "Very low confidence";
  }

  return (
    <Badge size="1" variant="soft" color={color}>
      {label} · {confidence}%
    </Badge>
  );
}

const PLAN_STEP_ICONS: Record<string, React.ReactNode> = {
  "AI Review": (
    <MagnifyingGlass size={16} weight="bold" className="text-puntt-accent-9" />
  ),
  "Next Steps": (
    <Users size={16} weight="bold" className="text-puntt-accent-9" />
  ),
  "Final Approval": (
    <Seal size={16} weight="bold" className="text-puntt-accent-9" />
  ),
};

function DraftPlanView({
  projectData,
  onSendMessage,
  actionsDisabled,
}: {
  projectData: AskPunttProjectData;
  onSendMessage?: (message: string) => void;
  actionsDisabled?: boolean;
}) {
  const { t } = useTranslation("ui");
  const planSteps = projectData.planSteps ?? [];
  const stages = (projectData.stages ?? []).filter(
    (s) => s.name && s.name !== "Final",
  );

  const [reviewMode, setReviewMode] = useState<"full" | "quick" | null>(null);
  const [selectedAreas, setSelectedAreas] = useState<Set<string>>(
    () => new Set(stages.map((s) => s.name)),
  );

  const toggleArea = (name: string) => {
    setSelectedAreas((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const handleApprove = () => {
    if (!onSendMessage) return;
    if (reviewMode === "quick" && selectedAreas.size > 0) {
      const areas = Array.from(selectedAreas).join(", ");
      onSendMessage(`Start project with a quick review focusing on: ${areas}`);
    } else {
      onSendMessage("Start project");
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 space-y-3">
        {planSteps.map((step, idx) => {
          const isReviewStep = idx === 0;
          return (
            <div
              key={step.title ?? idx}
              className="rounded-xl border border-puntt-neutral-gray-6 bg-puntt-neutral-gray-1 p-4"
            >
              <Flex align="center" gap="2.5" className="mb-2">
                <div className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-puntt-accent-2 ring-1 ring-puntt-accent-5/50">
                  {PLAN_STEP_ICONS[step.title] ?? (
                    <Rocket
                      size={14}
                      weight="bold"
                      className="text-puntt-accent-9"
                    />
                  )}
                </div>
                <Text
                  size="2"
                  weight="bold"
                  className="text-puntt-neutral-gray-12"
                >
                  {idx + 1}. {step.title}
                </Text>
              </Flex>
              <Text
                as="p"
                size="2"
                className="leading-relaxed text-puntt-neutral-gray-11"
              >
                {step.description}
              </Text>

              {isReviewStep && stages.length > 0 && (
                <div className="mt-3">
                  {onSendMessage && (
                    <Flex gap="2" className="mb-3">
                      <button
                        data-testid="project-status-pane-button-1158"
                        disabled={actionsDisabled}
                        onClick={() => {
                          setReviewMode("full");
                          setSelectedAreas(new Set(stages.map((s) => s.name)));
                        }}
                        className={`flex flex-1 flex-col items-start rounded-lg border px-3 py-2.5 text-left transition-all disabled:opacity-50 ${
                          reviewMode !== "quick"
                            ? "border-puntt-accent-7 bg-puntt-accent-2"
                            : "border-puntt-neutral-gray-6 bg-base-white hover:border-puntt-neutral-gray-6 hover:bg-puntt-neutral-gray-2"
                        }`}
                      >
                        <Text
                          size="2"
                          weight="bold"
                          className={
                            reviewMode !== "quick"
                              ? "text-puntt-accent-11"
                              : "text-puntt-neutral-gray-12"
                          }
                        >
                          {t("fullReview")}
                        </Text>
                        <Text size="1" className="text-puntt-neutral-gray-9">
                          {t("projectStatusPane.fullReviewEstimate", {
                            areas: stages.length,
                          })}
                        </Text>
                      </button>
                      <button
                        data-testid="project-status-pane-button-1185"
                        disabled={actionsDisabled}
                        onClick={() => setReviewMode("quick")}
                        className={`flex flex-1 flex-col items-start rounded-lg border px-3 py-2.5 text-left transition-all disabled:opacity-50 ${
                          reviewMode === "quick"
                            ? "border-puntt-accent-7 bg-puntt-accent-2"
                            : "border-puntt-neutral-gray-6 bg-base-white hover:border-puntt-neutral-gray-6 hover:bg-puntt-neutral-gray-2"
                        }`}
                      >
                        <Text
                          size="2"
                          weight="bold"
                          className={
                            reviewMode === "quick"
                              ? "text-puntt-accent-11"
                              : "text-puntt-neutral-gray-12"
                          }
                        >
                          {t("quickReview")}
                        </Text>
                        <Text size="1" className="text-puntt-neutral-gray-9">
                          {t("projectStatusPane.quickReviewEstimate")}
                        </Text>
                      </button>
                    </Flex>
                  )}

                  <Flex wrap="wrap" gap="1.5">
                    {stages.map((s) => {
                      const isSelected = selectedAreas.has(s.name);
                      const isQuickMode = reviewMode === "quick";
                      return (
                        <button
                          data-testid="project-status-pane-button-1217"
                          key={s.name}
                          type="button"
                          disabled={!isQuickMode || actionsDisabled}
                          onClick={() => toggleArea(s.name)}
                          className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-all ${
                            isQuickMode
                              ? isSelected
                                ? "border-puntt-accent-7 bg-puntt-accent-3 text-puntt-accent-11"
                                : "border-puntt-neutral-gray-6 bg-base-white text-puntt-neutral-gray-9 hover:border-puntt-neutral-gray-6"
                              : "border-puntt-neutral-gray-6 bg-puntt-neutral-gray-2 text-puntt-neutral-gray-11"
                          }`}
                        >
                          {isQuickMode && isSelected && (
                            <Check
                              size={10}
                              weight="bold"
                              className="mr-1 inline"
                            />
                          )}
                          {s.name}
                        </button>
                      );
                    })}
                  </Flex>
                  {reviewMode === "quick" && (
                    <Text
                      as="p"
                      size="1"
                      className="mt-2 text-puntt-neutral-gray-9"
                    >
                      {selectedAreas.size === 0
                        ? t("selectAtLeastOneArea")
                        : t("projectStatusPane.selectedAreasEstimate", {
                            selected: selectedAreas.size,
                            total: stages.length,
                            min: selectedAreas.size * 2,
                            max: selectedAreas.size * 3,
                          })}
                    </Text>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {projectData.aiRecommendation && (
          <div className="rounded-xl border border-puntt-accent-5/50 bg-puntt-accent-2/30 p-4">
            <Flex align="center" gap="2" className="mb-1.5">
              <Lightning
                size={14}
                weight="fill"
                className="text-puntt-accent-9"
              />
              <Text size="1" weight="bold" className="text-puntt-accent-11">
                {t("aiRecommendation")}
              </Text>
            </Flex>
            <Text
              size="2"
              className="leading-relaxed text-puntt-neutral-gray-12"
            >
              {projectData.aiRecommendation}
            </Text>
          </div>
        )}
      </div>

      {onSendMessage && (
        <div className="mt-5 border-t border-puntt-neutral-gray-6 pt-4">
          <Flex gap="2">
            <Button
              data-testid="project-status-pane-button-1284"
              size="2"
              className="flex-1 cursor-pointer"
              disabled={
                actionsDisabled ||
                (reviewMode === "quick" && selectedAreas.size === 0)
              }
              onClick={handleApprove}
            >
              <Play size={14} weight="fill" />
              {t("approveAndStart")}
            </Button>
            <Button
              data-testid="project-status-pane-button-1296"
              size="2"
              variant="outline"
              className="cursor-pointer"
              disabled={actionsDisabled}
              onClick={() =>
                onSendMessage("I want to change something about this plan")
              }
            >
              <PencilSimple size={14} weight="bold" />
              {t("edit")}
            </Button>
          </Flex>
        </div>
      )}
    </div>
  );
}

function TicketStatusDot({ ticketId }: { ticketId: string }) {
  const { ticket } = useTicketViewData(ticketId);
  if (!ticket) return null;
  const status = ticket.status ?? "wip";
  const color = TICKET_STATUS_COLORS[status] ?? "gray";
  const label = TICKET_STATUS_LABELS[status] ?? status;
  return (
    <Tooltip content={label}>
      <div
        className="size-2.5 rounded-full"
        style={{
          backgroundColor: `var(--${color}-9, var(--gray-9))`,
        }}
      />
    </Tooltip>
  );
}

export function ProjectStatusPane({
  projectData,
  activeTicketId,
  ticketIds,
  onTicketSelect,
  chatId,
  chatCreatedAt,
  onDismiss,
  onSendMessage,
  onProjectDataUpdate,
  actionsDisabled,
  isLoading,
  isKillerFlowStreaming,
}: ProjectStatusPaneProps) {
  const { t } = useTranslation("ui");
  const stages = projectData.stages ?? [];
  const routing = projectData.routing ?? [];
  const hasAnyTickets = !!activeTicketId || (ticketIds?.length ?? 0) > 0;
  const isPlanPending =
    stages.length > 0 &&
    stages.every((s) => s.status === "pending") &&
    !hasAnyTickets;
  const [actionLoading, setActionLoading] = useState(false);

  const handleOverrideStage = useCallback(
    async (stageName: string) => {
      setActionLoading(true);
      try {
        const result = await updateProjectAction(
          chatId,
          "override_stage",
          stageName,
        );
        onProjectDataUpdate?.(result.projectData);
      } catch (err) {
        console.error("Failed to override stage:", err);
      } finally {
        setActionLoading(false);
      }
    },
    [chatId, onProjectDataUpdate],
  );

  const handleCompleteProject = useCallback(async () => {
    setActionLoading(true);
    try {
      const result = await updateProjectAction(chatId, "complete_project");
      onProjectDataUpdate?.(result.projectData);
    } catch (err) {
      console.error("Failed to complete project:", err);
    } finally {
      setActionLoading(false);
    }
  }, [chatId, onProjectDataUpdate]);

  return (
    <div
      className="flex h-full flex-col overflow-hidden bg-base-white"
      data-testid="project-status-pane"
    >
      {/* Header — the title bar is locked to the canonical PaneHeader
          height so it lines up with the canvas / ticket / asset panes
          that render in the same right-side slot. Project metadata +
          description move into the meta strip BELOW so the title row
          never grows past the shared height. */}
      <PaneHeader
        region="project"
        leading={
          isLoading ? (
            <div className="size-2.5 shrink-0 animate-pulse rounded-full bg-puntt-accent-9 ring-2 ring-puntt-accent-4" />
          ) : undefined
        }
        title={
          <span className="flex min-w-0 items-center gap-2">
            <span className="truncate text-[13px] font-semibold normal-case tracking-normal text-puntt-neutral-gray-12">
              {projectData.name || (
                <span className="inline-block h-3.5 w-48 animate-pulse rounded bg-puntt-neutral-gray-4" />
              )}
            </span>
            {isPlanPending && (
              <Badge size="1" variant="outline" color="blue">
                {t("draft")}
              </Badge>
            )}
          </span>
        }
        trailing={
          onDismiss ? (
            <PaneHeaderIconButton
              onClick={onDismiss}
              data-testid="project-status-dismiss-btn"
              aria-label={t("collapseProjectPane")}
            >
              <X size={PANE_HEADER_ICON_SIZE} aria-hidden="true" />
            </PaneHeaderIconButton>
          ) : undefined
        }
      />
      <div
        className="shrink-0 border-b border-puntt-neutral-gray-6 px-5 py-2.5"
        data-testid="project-status-meta"
      >
        <Flex align="center" gap="2">
          <Text size="2" className="text-puntt-neutral-gray-9">
            {(projectData.markets ?? []).length > 0
              ? (projectData.markets ?? []).join(", ") + " · "
              : ""}
            {projectData.projectType}
          </Text>
          {projectData.confidence != null && (
            <ConfidenceBadge confidence={projectData.confidence} />
          )}
        </Flex>
        {projectData.description && (
          <Text size="2" className="mt-1 text-puntt-neutral-gray-10">
            {projectData.description}
          </Text>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-5">
        {isPlanPending && (projectData.planSteps?.length ?? 0) > 0 ? (
          <DraftPlanView
            projectData={projectData}
            onSendMessage={onSendMessage}
            actionsDisabled={actionsDisabled}
          />
        ) : isPlanPending && isLoading ? (
          <div className="flex h-full flex-col">
            <div className="flex-1 space-y-3">
              {[...Array(3)].map((_, i) => (
                <div
                  key={i}
                  className="rounded-xl border border-puntt-neutral-gray-6 bg-puntt-neutral-gray-1 p-4"
                >
                  <div className="mb-3 flex items-center gap-2.5">
                    <div className="size-7 animate-pulse rounded-lg bg-puntt-neutral-gray-4" />
                    <div className="h-4 w-32 animate-pulse rounded bg-puntt-neutral-gray-4" />
                  </div>
                  <div className="space-y-2">
                    <div className="h-3 w-full animate-pulse rounded bg-puntt-neutral-gray-3" />
                    <div className="h-3 w-4/5 animate-pulse rounded bg-puntt-neutral-gray-3" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <>
            {(ticketIds?.length ?? 0) > 0 ? (
              <div className="mb-5" data-testid="project-tickets-section">
                <Flex align="center" justify="between" className="mb-2">
                  <Text
                    size="1"
                    weight="bold"
                    className="uppercase tracking-wider text-puntt-neutral-gray-9"
                    data-testid="project-tickets-section-title"
                  >
                    {ticketIds!.length > 1
                      ? t("projectStatusPane.reviews", {
                          total: ticketIds!.length,
                        })
                      : t("projectStatusPane.tickets", {
                          total: ticketIds!.length,
                        })}
                  </Text>
                  <Flex align="center" gap="1">
                    {ticketIds!.map((id) => (
                      <TicketStatusDot key={id} ticketId={id} />
                    ))}
                  </Flex>
                </Flex>
                {ticketIds!.map((id) => (
                  <TicketCard
                    key={id}
                    ticketId={id}
                    stages={stages}
                    onSelect={onTicketSelect}
                    ticketFindings={projectData.ticketFindings}
                    ticketCompliance={projectData.ticketCompliance}
                    isReviewing={isKillerFlowStreaming}
                    showExternalLink={ticketIds!.length <= 1}
                  />
                ))}
              </div>
            ) : activeTicketId ? (
              <TicketCard
                ticketId={activeTicketId}
                stages={stages}
                onSelect={onTicketSelect}
                ticketFindings={projectData.ticketFindings}
                ticketCompliance={projectData.ticketCompliance}
                isReviewing={isKillerFlowStreaming}
              />
            ) : null}

            {/* Compliance Summary — placed above Findings Summary so verdict
                counts are the headline signal for compliance projects.
                Suppressed during the killer-flow typewriter so the
                resolution lands all at once when streaming completes. */}
            {!isKillerFlowStreaming &&
              (projectData.complianceSummary ? (
                <ComplianceSummaryBlock
                  summary={projectData.complianceSummary}
                />
              ) : (
                <ComplianceAggregationErrorHint projectData={projectData} />
              ))}

            {/* Findings Summary — positioned before timeline for visual priority.
                During killer-flow streaming we hide the resolved
                summary and instead render a "reviewers running"
                placeholder so the buyer doesn't see the spoiler
                before the typewriter completes. */}
            {isKillerFlowStreaming ? (
              <div
                className="mb-5 rounded-lg border border-puntt-neutral-gray-6 bg-puntt-neutral-gray-2/30 p-3"
                data-testid="project-status-findings-reviewing"
              >
                <Flex align="start" gap="2">
                  <CircleNotch
                    size={14}
                    className="mt-0.5 shrink-0 animate-spin text-puntt-accent-9"
                  />
                  <Text size="2" className="text-puntt-neutral-gray-10">
                    {t("projectStatusPane.reviewingInParallel")}
                  </Text>
                </Flex>
              </div>
            ) : projectData.findingsSummary ? (
              projectData.ticketFindings?.every(
                (f) => f.severity === "clean",
              ) ? (
                <div className="border-green-200 bg-green-50 mb-5 rounded-lg border p-3">
                  <Flex align="start" gap="2">
                    <CheckCircle
                      size={14}
                      weight="fill"
                      className="text-green-600 mt-0.5 shrink-0"
                    />
                    <Text size="2" weight="medium" className="text-green-800">
                      {projectData.findingsSummary}
                    </Text>
                  </Flex>
                </div>
              ) : (
                <div className="mb-5 rounded-lg border border-puntt-neutral-gray-6 bg-puntt-neutral-gray-2/30 p-4">
                  <Flex align="center" gap="2" className="mb-2">
                    <MagnifyingGlass
                      size={14}
                      weight="fill"
                      className="text-puntt-accent-9"
                    />
                    <Text
                      size="2"
                      weight="bold"
                      className="text-puntt-neutral-gray-12"
                    >
                      {t("keyFindings")}
                    </Text>
                  </Flex>
                  <Markdown className="text-sm leading-relaxed text-puntt-neutral-gray-11">
                    {projectData.findingsSummary}
                  </Markdown>
                </div>
              )
            ) : stages.some((s) => s.status === "active") ? (
              <div className="mb-5 rounded-lg border border-puntt-neutral-gray-6 bg-puntt-neutral-gray-2/30 p-3">
                <Flex align="start" gap="2">
                  <CircleNotch
                    size={14}
                    className="mt-0.5 shrink-0 animate-spin text-puntt-accent-9"
                  />
                  <Text size="2" className="text-puntt-neutral-gray-10">
                    {t("projectStatusPane.findingsWillAppear")}
                  </Text>
                </Flex>
              </div>
            ) : null}

            {/* Timeline — hidden for now, code preserved */}
            {false && (
              <>
                <Text
                  size="1"
                  weight="bold"
                  className="mb-3 uppercase tracking-wider text-puntt-neutral-gray-9"
                >
                  {t("timeline")}
                </Text>
                <TimelineBlock
                  chatCreatedAt={chatCreatedAt}
                  targetDeadline={projectData.targetDeadline}
                  completedAt={projectData.completedAt}
                />
              </>
            )}

            {/* Action Status — hidden for now, code preserved */}
            {false &&
              (stages.length > 0 ? (
                <ActionStatus
                  stages={stages}
                  routing={routing}
                  hasTicket={hasAnyTickets}
                  onSendMessage={onSendMessage}
                  onCompleteProject={handleCompleteProject}
                  onOverrideAll={handleCompleteProject}
                  actionsDisabled={actionsDisabled || actionLoading}
                />
              ) : (
                isLoading && (
                  <div className="mb-5 rounded-lg border border-puntt-neutral-gray-6 bg-puntt-neutral-gray-2 px-3 py-2.5">
                    <Flex align="center" gap="2">
                      <span className="size-2.5 shrink-0 animate-pulse rounded-full bg-puntt-accent-9" />
                      <Text
                        size="2"
                        weight="medium"
                        className="text-puntt-neutral-gray-9"
                      >
                        {t("projectStatusPane.buildingProjectPlan")}
                      </Text>
                    </Flex>
                  </div>
                )
              ))}

            {/* Stage Progress Bar — hidden for now, code preserved */}
            {false && (
              <div className="mb-5" data-testid="project-status-stage-bar">
                {stages.length > 0 ? (
                  <>
                    <Flex gap="1">
                      {stages.map((stage, i) => (
                        <div
                          key={stage.name ?? i}
                          className={stageBarClasses(stage)}
                          title={`${stage.name ?? "Stage"}: ${stage.status}`}
                        />
                      ))}
                    </Flex>
                    <Flex gap="1" className="mt-1">
                      {stages.map((stage, i) => (
                        <Text
                          key={stage.name ?? i}
                          size="1"
                          weight={stageLabelWeight(stage.status)}
                          className={`flex-1 text-center ${stageLabelColor(stage.status)}`}
                        >
                          {stage.name}
                        </Text>
                      ))}
                    </Flex>
                  </>
                ) : (
                  isLoading && (
                    <Flex gap="1">
                      {[...Array(5)].map((_, i) => (
                        <div
                          key={i}
                          className="h-2.5 flex-1 animate-pulse rounded-full bg-puntt-neutral-gray-4"
                        />
                      ))}
                    </Flex>
                  )
                )}
              </div>
            )}

            {/* Routing Section — hidden for now, code preserved */}
            {false && (
              <>
                <Flex
                  align="center"
                  justify="between"
                  className="mb-2 border-t border-puntt-neutral-gray-6 pt-4"
                >
                  <Text
                    size="1"
                    weight="bold"
                    className="uppercase tracking-wider text-puntt-neutral-gray-9"
                  >
                    {t("projectStatusPane.routing")}
                    {routing.length > 0 && (
                      <>
                        {" · "}
                        {t("projectStatusPane.reviewersCount", {
                          total: routing.length,
                        })}
                      </>
                    )}
                  </Text>
                </Flex>
                {routing.length > 0 ? (
                  <div className="divide-y divide-puntt-neutral-gray-3">
                    {routing.map((reviewer, i) => (
                      <ReviewerRow
                        key={reviewer.name ?? i}
                        reviewer={reviewer}
                        onOverride={(role) => handleOverrideStage(role)}
                        actionsDisabled={actionsDisabled || actionLoading}
                      />
                    ))}
                  </div>
                ) : (
                  isLoading && (
                    <div className="space-y-3">
                      {[...Array(3)].map((_, i) => (
                        <Flex key={i} align="center" gap="3" className="py-2.5">
                          <div className="size-8 animate-pulse rounded-full bg-puntt-neutral-gray-4" />
                          <div className="min-w-0 flex-1 space-y-1.5">
                            <div className="h-3.5 w-28 animate-pulse rounded bg-puntt-neutral-gray-4" />
                            <div className="h-3 w-16 animate-pulse rounded bg-puntt-neutral-gray-3" />
                          </div>
                          <div className="h-5 w-16 animate-pulse rounded-full bg-puntt-neutral-gray-4" />
                        </Flex>
                      ))}
                    </div>
                  )
                )}
              </>
            )}

            {/* AI Recommendation — hidden for now, code preserved */}
            {false &&
              (projectData.findingsSummary || projectData.aiRecommendation) && (
                <div
                  className="mt-5 rounded-xl border border-puntt-accent-5/50 bg-puntt-accent-2/30 p-4"
                  data-testid="project-status-ai-recommendation"
                >
                  <Flex align="center" gap="2" className="mb-1.5">
                    <Lightning
                      size={14}
                      weight="fill"
                      className="text-puntt-accent-9"
                    />
                    <Text
                      size="1"
                      weight="bold"
                      className="text-puntt-accent-11"
                    >
                      {t("aiRecommendation")}
                    </Text>
                  </Flex>
                  <Markdown className="text-sm leading-relaxed text-puntt-neutral-gray-12">
                    {projectData.findingsSummary ||
                      projectData.aiRecommendation}
                  </Markdown>
                </div>
              )}
          </>
        )}
      </div>
    </div>
  );
}
